import { PayloadAction } from '@reduxjs/toolkit'
import { SagaIterator } from 'redux-saga'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  getPermissionByTenantId,
  hideLoader,
  showLoader,
  updateClientPermissionState,
} from '../slices'
import { getClientPermissionsByPortalIdApi } from '../apis'
import {
  PermissionsDataType,
  PermissionsResponseDataType,
  ProgramPermissionsDataType,
  ProgramPermissionsResponseDataType,
} from '../types/permissions.types'

function* fetchClientPermissionsById(
  _action: PayloadAction<number>
): SagaIterator {
  try {
    yield put(showLoader())
    const isSuperAdmin = yield select((state) => state.authUser.user.superAdmin)
    const response = yield call(getClientPermissionsByPortalIdApi)
    const permissions: ProgramPermissionsDataType = {}
    const globalPermissions: PermissionsDataType = {}
    const programs: { [key: string]: string } = {}
    const permissionServices: { [key: string]: string } = {}
    const programServices: {
      [key: string]: number[]
    } = {}
    const permissionNameIdMapping: {
      permission_name: string
      permission_id: number
    }[] = []

    const seenPermissions = new Set<string>()
    if (response?.data?.program_permissions) {
      if (
        Array.isArray(response.data.program_permissions) &&
        response.data.program_permissions.length
      ) {
        response.data.program_permissions.forEach(
          (programPermission: ProgramPermissionsResponseDataType) => {
            programs[programPermission.program_id] =
              programPermission.operational_program_name
            if (!permissions[programPermission.program_id])
              permissions[programPermission.program_id] = {}
            programPermission.permissions.forEach((permission) => {
              if (
                !permissions[programPermission.program_id][
                  permission.permission_name
                ]
              )
                permissions[programPermission.program_id][
                  permission.permission_name
                ] = {
                  'View Only': false,
                  Manage: false,
                }
              permissions[programPermission.program_id][
                permission.permission_name
              ][permission.operation_name] = Boolean(permission.operation_value)

              const key = `${permission.permission_id}_${permission.permission_name}`
              if (!seenPermissions.has(key)) {
                seenPermissions.add(key)
                permissionNameIdMapping.push({
                  permission_name: permission.permission_name,
                  permission_id: permission.permission_id,
                })
              }

              permission.services.forEach((service) => {
                if (!permissionServices[service.service_name]) {
                  permissionServices[service.service_name] =
                    permission.permission_name
                }
                if (permission.operation_value) {
                  if (!programServices[programPermission.program_id]) {
                    programServices[programPermission.program_id] = [
                      service.service_id,
                    ]
                  } else {
                    if (
                      !programServices[programPermission.program_id].includes(
                        service.service_id
                      )
                    ) {
                      programServices[programPermission.program_id].push(
                        service.service_id
                      )
                    }
                  }
                }
              })
            })
          }
        )
      }
    }
    if (Array.isArray(response?.data?.global_permissions)) {
      response.data.global_permissions.forEach(
        (globalPermission: PermissionsResponseDataType) => {
          if (!globalPermissions[globalPermission.permission_name])
            globalPermissions[globalPermission.permission_name] = {
              'View Only': false,
              Manage: false,
            }
          globalPermissions[globalPermission.permission_name][
            globalPermission.operation_name
          ] = Boolean(globalPermission.operation_value)
        }
      )
    }
    yield put(
      updateClientPermissionState({
        programPermissions: permissions,
        hasViewPermission: true,
        programs: programs,
        permissionServices: permissionServices,
        programServices: programServices,
        permissionNameIdMapping: permissionNameIdMapping,
        globalPermissions: globalPermissions,
      })
    )
    localStorage.setItem(
      'has_sensitive_permission',
      isSuperAdmin ||
        globalPermissions?.['Manage Client Controlled Data']?.Manage
        ? 'true'
        : 'false'
    )
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

export function* watchFetchClientPermissionsById() {
  yield takeLatest(getPermissionByTenantId.type, fetchClientPermissionsById)
}
